import MobileDetect from 'mobile-detect';
import { websocket } from 'core/websocket/Websocket';
import { setUserSentry, setOrganizationSentry } from 'core/Sentry';
import { initIntercom } from 'core/Intercom';
import { clearSessionIntercom } from 'core/Intercom/Intercom';
import globalServices from 'services/global';
import { useOrganizationStore, useUserStore } from 'store/modules';
import { isAxiosError } from 'axios';

const detect = new MobileDetect(window.navigator.userAgent);
const platform = detect.os();
const APP_API_FALLBACK_ENABLED = process.env.APP_API_FALLBACK_ENABLED;

export default async function (to, from, next) {
    // This guard is fired prior to every native HTTP request to load the application.
    // It will set the user, as well as other various application properties if the
    // user is authenticated.

    const userStore = useUserStore();
    const organizationStore = useOrganizationStore();

    if (userStore.user === null) {
        try {
            clearSessionIntercom();

            const data = await userStore.updateAuthRefresh(Boolean(APP_API_FALLBACK_ENABLED));

            if (data.is_login) {
                const token = data.token?.access_token;
                let hasOid = true;

                if (token) {
                    await userStore.setAppToken(data.token?.access_token);

                    try {
                        const [, tokenData] = token.split('.');
                        const parsedData = JSON.parse(atob(tokenData));

                        hasOid = Boolean(parsedData.oid);
                    } catch (e) {
                        console.error(e);
                    }
                }

                if (!hasOid) {
                    window.DesignV2API.init({
                        is_enable_new_design: true,
                        sign_up_v2: true,
                    });

                    await userStore.updateAppLoading(false);
                } else {
                    websocket.connect(localStorage.getItem('token'));
                    await userStore.updateAppLoading(true);
                    const [featureFlags, userSettings] = await Promise.all([
                        organizationStore.getUsersFeatureFlags(),
                        userStore.getUsersSettings(),
                    ]);
                    const salesmsgDesignV2 = Boolean(
                        userSettings?.find((setting) => setting.key === 'salesmsg-design-v2')
                            ?.settings['active']
                    );

                    if (window.DesignV2API) {
                        window.DesignV2API.init({
                            is_enable_new_design: Boolean(
                                featureFlags.show_new_design_only || salesmsgDesignV2
                            ),
                            salesmsg_design_v2_salesforce: Boolean(
                                featureFlags.salesmsg_design_v2_salesforce
                            ),
                            salesmsg_design_v2_compliance_toll_free: Boolean(
                                featureFlags.salesmsg_design_v2_compliance_toll_free
                            ),
                            salesmsg_design_v2_calendly_page: Boolean(
                                featureFlags.salesmsg_design_v2_calendly_page
                            ),
                            hubspot_workflow_statistics: Boolean(
                                featureFlags.hubspot_workflow_statistics
                            ),
                            salesmsg_design_v2_general_org_setting: Boolean(
                                featureFlags.general_org_setting
                            ),
                            salesmsg_design_v2_email_to_sms_org_setting: Boolean(
                                featureFlags.email_to_sms_org_setting
                            ),
                            billing_upgrade_v2: Boolean(featureFlags.billing_upgrade_v2),
                            billing_overview_v2: Boolean(featureFlags.billing_overview_v2),
                            zoho_integration: Boolean(featureFlags.zoho_integration),
                            settings_inboxes_manage_inbox_calling_v2: Boolean(
                                featureFlags.settings_inboxes_manage_inbox_calling_v2
                            ),
                            webhook_setting: Boolean(featureFlags.webhook_setting),
                            search_results_page: Boolean(featureFlags.search_results_page),
                            integrations_list_v2: featureFlags.integrations_list_v2,

                            tag: Boolean(featureFlags.tag),
                            salesmsg_design_v2_clients: Boolean(
                                featureFlags.salesmsg_design_v2_clients
                            ),
                            salesmsg_design_v2_teams: Boolean(
                                featureFlags.salesmsg_design_v2_teams
                            ),
                            chat_widget_setting_v2: Boolean(featureFlags.chat_widget_setting_v2),
                            url_shortener_design_v2: Boolean(featureFlags.url_shortener_design_v2),
                        });
                    }

                    if (salesmsgDesignV2) {
                        await Promise.all([
                            globalServices.getBusinessHours(),
                            globalServices.getSubscription(),
                            globalServices.getOrganization(),
                            globalServices.getOrganizationSettings(),
                            globalServices.getUsersConfig(),
                            globalServices.getUsersPermissions(),
                            globalServices.getOrganizationNumber(),
                            globalServices.getNumbersIntegration(),
                            globalServices.getUserOwner(),
                            globalServices.getAllTeams(),
                            globalServices.getTeams({ origin: true }),
                            globalServices.loadMultiOrganizationsList(),
                        ]);
                    } else {
                        await Promise.all([
                            globalServices.getSubscription(),
                            globalServices.getUserMe(),
                            globalServices.loadMultiOrganizationsList(),
                        ]);
                    }

                    const user = userStore.user;

                    setUserSentry({
                        id: user.id,
                        email: user.email,
                        username: user.fullName,
                    });

                    const organization = organizationStore.organization;
                    setOrganizationSentry(organization);

                    setUserSentry({
                        id: user.id,
                        email: user.email,
                        username: user.fullName,
                    });

                    userStore.updateAppLoading(false);
                }
            } else {
                if (window.DesignV2API) {
                    window.DesignV2API.init({
                        sign_up_v2: true,
                    });
                }

                userStore.resetStateUser();
                userStore.updateAppLoading(false);
            }

            initIntercom();

            if (
                (platform === 'AndroidOS' || platform === 'iOS') &&
                !localStorage.getItem('landing_mobile_exist') &&
                to.name !== 'auth.login' &&
                to.name !== 'auth.reset' &&
                to.name !== 'register'
            ) {
                next('/landing-mobile');
            } else {
                next();
            }
        } catch (error) {
            console.error(error);

            if (isAxiosError(error)) {
                if (error.code === 'ERR_NETWORK') {
                    throw error;
                }
            }

            window.localStorage.removeItem('soundTabIds');

            next();
        }
    } else {
        next();
    }
}
